import { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, Loader2, Sparkles, CheckCircle2, XCircle, HelpCircle } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { useToast } from "@/hooks/use-toast";
import { useQuery } from "@tanstack/react-query";

interface Props {
  classes: { id: string; name: string }[];
  onSuccess: () => void;
}

const typeLabels: Record<string, string> = { multiple_choice: "اختياري", true_false: "صح/خطأ", essay: "مقالي" };
const diffLabels: Record<string, string> = { easy: "سهل", medium: "متوسط", hard: "صعب" };
const diffColors: Record<string, string> = {
  easy: "bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400",
  medium: "bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400",
  hard: "bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400",
};

export default function AddExamDialog({ classes, onSuccess }: Props) {
  const [open, setOpen] = useState(false);
  const [step, setStep] = useState<1 | 2>(1);
  const [title, setTitle] = useState("");
  const [classId, setClassId] = useState("");
  const [scheduledAt, setScheduledAt] = useState("");
  const [duration, setDuration] = useState("30");
  const [selectedQuestionIds, setSelectedQuestionIds] = useState<Set<string>>(new Set());
  const [saving, setSaving] = useState(false);
  const { profile } = useAuth();
  const { toast } = useToast();

  const nurseryId = profile?.nursery_id;

  const { data: questions } = useQuery({
    queryKey: ["all-questions", nurseryId],
    queryFn: async () => {
      const { data } = await supabase
        .from("questions")
        .select("*")
        .eq("nursery_id", nurseryId!)
        .order("created_at", { ascending: false });
      return data || [];
    },
    enabled: open && !!nurseryId,
  });

  const toggleQuestion = (id: string) => {
    setSelectedQuestionIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const selectAll = () => {
    if (selectedQuestionIds.size === (questions || []).length) {
      setSelectedQuestionIds(new Set());
    } else {
      setSelectedQuestionIds(new Set((questions || []).map((q: any) => q.id)));
    }
  };

  const handleSave = async () => {
    if (!nurseryId || !title.trim()) return;
    if (selectedQuestionIds.size === 0) {
      toast({ title: "خطأ", description: "اختر سؤال واحد على الأقل", variant: "destructive" });
      return;
    }

    setSaving(true);
    try {
      // Calculate max score
      const selectedQs = (questions || []).filter((q: any) => selectedQuestionIds.has(q.id));
      const maxScore = selectedQs.length; // 1 point per question

      const { data: exam, error } = await supabase.from("exams").insert({
        title: title.trim(),
        class_id: classId || null,
        scheduled_at: scheduledAt || null,
        nursery_id: nurseryId,
        duration_minutes: parseInt(duration) || null,
        max_score: maxScore,
        is_interactive: true,
      }).select("id").single();

      if (error || !exam) throw error || new Error("Failed");

      // Link questions to exam
      const examQuestions = Array.from(selectedQuestionIds).map((qId, i) => ({
        exam_id: exam.id,
        question_id: qId,
        sort_order: i,
        points: 1,
      }));

      const { error: linkError } = await supabase.from("exam_questions").insert(examQuestions);
      if (linkError) throw linkError;

      toast({ title: `تم إنشاء الامتحان مع ${selectedQuestionIds.size} سؤال` });
      resetForm();
      setOpen(false);
      onSuccess();
    } catch (e: any) {
      toast({ title: "خطأ", description: e.message, variant: "destructive" });
    } finally {
      setSaving(false);
    }
  };

  const resetForm = () => {
    setStep(1);
    setTitle("");
    setClassId("");
    setScheduledAt("");
    setDuration("30");
    setSelectedQuestionIds(new Set());
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (!o) resetForm(); }}>
      <DialogTrigger asChild>
        <Button variant="outline">
          <Plus className="w-4 h-4 ml-2" />
          إضافة امتحان
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-xl max-h-[90vh] flex flex-col" dir="rtl">
        <DialogHeader>
          <DialogTitle>{step === 1 ? "إنشاء امتحان تفاعلي" : "اختيار الأسئلة"}</DialogTitle>
        </DialogHeader>

        {step === 1 ? (
          <div className="space-y-4">
            <div>
              <Label>عنوان الامتحان *</Label>
              <Input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="اختبار الحروف" />
            </div>
            <div>
              <Label>الفصل *</Label>
              <Select value={classId} onValueChange={setClassId}>
                <SelectTrigger><SelectValue placeholder="اختر الفصل" /></SelectTrigger>
                <SelectContent>
                  {classes.map((c) => (
                    <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>تاريخ الامتحان</Label>
                <Input type="datetime-local" value={scheduledAt} onChange={(e) => setScheduledAt(e.target.value)} />
              </div>
              <div>
                <Label>المدة (دقائق)</Label>
                <Select value={duration} onValueChange={setDuration}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {["10", "15", "20", "30", "45", "60"].map((d) => (
                      <SelectItem key={d} value={d}>{d} دقيقة</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <Button
              className="w-full"
              disabled={!title.trim() || !classId}
              onClick={() => setStep(2)}
            >
              التالي: اختيار الأسئلة
            </Button>
          </div>
        ) : (
          <div className="flex flex-col flex-1 min-h-0 space-y-3">
            <div className="flex items-center justify-between">
              <p className="text-sm text-muted-foreground">
                {selectedQuestionIds.size} سؤال محدد من {(questions || []).length}
              </p>
              <div className="flex gap-2">
                <Button variant="ghost" size="sm" onClick={selectAll}>
                  {selectedQuestionIds.size === (questions || []).length ? "إلغاء الكل" : "تحديد الكل"}
                </Button>
                <Button variant="outline" size="sm" onClick={() => setStep(1)}>رجوع</Button>
              </div>
            </div>

            <ScrollArea className="flex-1 max-h-[45vh]">
              {(questions || []).length === 0 ? (
                <div className="text-center py-8 text-muted-foreground text-sm">
                  <Sparkles className="w-8 h-8 mx-auto mb-2 opacity-50" />
                  لا توجد أسئلة في البنك بعد. ولّد أسئلة من الدروس أولاً
                </div>
              ) : (
                <div className="space-y-2 pl-2">
                  {(questions || []).map((q: any, i: number) => (
                    <div
                      key={q.id}
                      className={`border rounded-lg p-3 cursor-pointer transition-colors ${
                        selectedQuestionIds.has(q.id) ? "border-primary bg-primary/5" : "hover:bg-muted/50"
                      }`}
                      onClick={() => toggleQuestion(q.id)}
                    >
                      <div className="flex items-start gap-2">
                        <Checkbox
                          checked={selectedQuestionIds.has(q.id)}
                          onCheckedChange={() => toggleQuestion(q.id)}
                          className="mt-0.5"
                        />
                        <div className="flex-1 min-w-0">
                          <p className="text-sm">{q.question_text}</p>
                          <div className="flex gap-1.5 mt-1.5 flex-wrap">
                            <Badge variant="outline" className="text-xs">
                              {typeLabels[q.question_type] || q.question_type}
                            </Badge>
                            <Badge className={`text-xs ${diffColors[q.difficulty] || ""}`}>
                              {diffLabels[q.difficulty] || q.difficulty}
                            </Badge>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </ScrollArea>

            <Button
              className="w-full"
              disabled={saving || selectedQuestionIds.size === 0}
              onClick={handleSave}
            >
              {saving ? (
                <><Loader2 className="w-4 h-4 animate-spin ml-2" />جاري الإنشاء...</>
              ) : (
                `إنشاء الامتحان (${selectedQuestionIds.size} سؤال)`
              )}
            </Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
